/* eslint-disable react-hooks/exhaustive-deps */
import { useHistory } from "react-router-dom";

import {
  Create,
  Loading,
  SaveButton,
  SimpleForm,
  Toolbar,
  TopToolbar,
  useNotify,
} from "react-admin";

import { BackButton } from "../../../../components/common/BackButton";
import { ForceTitle } from "../../../../components/common/ForceTitle";
import { HelpButton } from "../../../../components/common/HelpButton";

import { ResourceType } from "../../../../utils/constants";
import { deepRedirect, getBackPath } from "../../../../utils/utils";
import { logError, logHistory } from "../../../../utils/appUtils";
import { ChargeEvent, FreeDaysPolicy } from "./chargeRuleConstants";
import {
  getStoragePeersInSameScope,
  normalizeFreeCars,
  normalizeGraduatedRates,
  validateChargeRule,
} from "./ChargeRuleUtils";
import { ChargeRuleForm } from "./ChargeRuleForm";

const ChargeRuleCreateActions = ({ backPath, ...props }) => (
  <TopToolbar>
    <HelpButton>
      <div>
        <h3>Charge Rule Help</h3>
        <p>Create a new charge rule for this account.</p>
        <p>Charge rules define when and how to bill customers for services.</p>
      </div>
    </HelpButton>
    <BackButton {...props} backPath={backPath} />
  </TopToolbar>
);

const ChargeRuleCreateToolbar = ({ backPath, account, ...props }) => {
  const history = useHistory();
  const notify = useNotify();

  const transform = (data) => {
    data = { ...data };
    debugger;

    // Set required fields
    data.active = data.active || "true";
    data.groups = account.groups;
    data.reloadYardId = account.reloadYardId;
    data.accountId = account.id;

    // Convert numeric fields - only if they have valid values
    if (data.minCharge != null && data.minCharge !== "")
      data.minCharge = parseFloat(data.minCharge);
    if (data.maxCharge != null && data.maxCharge !== "")
      data.maxCharge = parseFloat(data.maxCharge);
    if (!data.freeDays) data.freeDays = 0;
    data.freeCars = normalizeFreeCars(data);
    data.graduatedRates = normalizeGraduatedRates(data.graduatedRates);

    delete data.reloadYardCommodity;
    delete data.service;

    return data;
  };

  const onSuccess = ({ data }) => {
    const historyNote = {
      parentId: data.id,
      parentType: ResourceType.CHARGE_RULE,
      text: "Billing rule created.",
    };
    logHistory(historyNote);

    notify("Rule created successfully.", "success");
    deepRedirect(history, backPath);
  };

  const onFailure = (error) => {
    logError("chargeRules:ChargeRuleCreate:onFailure", error, notify);
  };

  return (
    <Toolbar {...props}>
      <SaveButton
        label="Save"
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
    </Toolbar>
  );
};

export const validateChargeRuleForm = (record, allChargeRules) => {
  const errors = {};

  if (
    record?.event &&
    record?.serviceId &&
    record?.billingUOM &&
    allChargeRules?.length > 0
  ) {
    const validation = validateChargeRule(record, allChargeRules);
    if (validation._error) {
      // surface the blocking error on the key WHAT fields + set on present filters
      const msg = validation._error;
      errors.event = msg;
      errors.serviceId = msg;
      errors.billingUOM = msg;
      if (record.carrierType) errors.carrierType = msg;
      if (record.reloadYardCommodityId) errors.reloadYardCommodityId = msg;
      if (record.areaId) errors.areaId = msg;
      if (record.zoneId) errors.zoneId = msg;
    }
  }

  // Keep frequency / fee accrual consistent across STORAGE rules that share a
  // service / area / zone. Free days are intentionally excluded: a rule scoped
  // to a load type may carry its own free days inside the same location.
  // Inputs are disabled if defaults applied, so user cannot override them
  const skipStorageScopeConsistency = record?.id;
  if (
    !skipStorageScopeConsistency &&
    record?.event === ChargeEvent.STORAGE &&
    Array.isArray(allChargeRules)
  ) {
    const peers = getStoragePeersInSameScope(record, allChargeRules);

    if (peers.length > 0) {
      const baseline =
        peers.find((p) => p.frequency || p.feeAccrual) || peers[0];

      if (baseline) {
        if (
          baseline.frequency &&
          record.frequency &&
          baseline.frequency !== record.frequency
        ) {
          errors.frequency = `Must match existing Storage frequency for this service/area/zone (${baseline.frequency}).`;
        }
        if (
          baseline.feeAccrual &&
          record.feeAccrual &&
          baseline.feeAccrual !== record.feeAccrual
        ) {
          errors.feeAccrual = `Must match existing Storage accrual for this service/area/zone (${baseline.feeAccrual}).`;
        }
      }
    }
  }

  return errors;
};

export const ChargeRuleCreate = (props) => {
  const backPath = getBackPath(props);
  const account = props.location?.state?.account;
  const allChargeRules = props.location?.state?.allChargeRules;
  const initialValues = {
    active: "true",
    groups: account?.groups,
    reloadYardId: account?.reloadYardId,
    accountId: account?.id,
    freeDaysPolicy: FreeDaysPolicy.DAYS,
  };

  if (!account) return <Loading />;
  return (
    <Create
      {...props}
      title={<ForceTitle title="New Charge Rule" />}
      actions={<ChargeRuleCreateActions backPath={backPath} />}
    >
      <SimpleForm
        variant="outlined"
        initialValues={initialValues}
        validate={(record) => validateChargeRuleForm(record, allChargeRules)}
        toolbar={
          <ChargeRuleCreateToolbar
            {...props}
            backPath={backPath}
            account={account}
          />
        }
      >
        <ChargeRuleForm account={account} allChargeRules={allChargeRules} />
      </SimpleForm>
    </Create>
  );
};
